import { useState } from "react";
import { Shield } from "@mui/icons-material";
import { Button, Checkbox, InputLabel } from "@mui/material";
import { Input } from "../ui/input";

import PhoneInputWithCountry from "../ui/PhoneInputWithCountry";
import { getOtpRequestCooldownMessage, isAccountAlreadyExistsError, isOtpRequestCooldownError, requestOtp, saveScoutSignupLead, stampOtpSessionData } from "../../services/otp";
import { emitSnackbar } from "../../lib/snackbar-emitter";
import manageErrors from "../../lib/error-management";
import { getPhoneDigits, isValidBrazilianPhone, isValidEmail } from "../../utils";

interface Step1Props {
  onNext: () => void;
  onLogin: (phone?: string) => void;
  setSession: (session_data: any) => void;
}

export let requestSession: () => Promise<void> = () => Promise.resolve()

const Step1AdminData = ({ onNext, onLogin, setSession }: Step1Props) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("BR");
  const [newsletter, setNewsletter] = useState(false);
  const [loading, setLoading] = useState(false)
  const [monitoringPhone, setMonitoringPhone] = useState("");
  const [monitoringPhoneCountryCode, setMonitoringPhoneCountryCode] = useState("BR");
  const [testMyself, setTestMyself] = useState(false);

  const normalizedEmail = email.trim();
  const emailIsValid = isValidEmail(normalizedEmail);
  const phoneDigits = getPhoneDigits(phone);
  const phoneIsValid = phoneDigits.length > 0 && isValidBrazilianPhone(phone, phoneCountryCode);
  const monitoringPhoneValue = testMyself ? phone : monitoringPhone;
  const monitoringPhoneCountry = testMyself ? phoneCountryCode : monitoringPhoneCountryCode;
  const monitoringPhoneDigits = getPhoneDigits(monitoringPhoneValue);
  const monitoringPhoneIsValid = monitoringPhoneDigits.length > 0 && isValidBrazilianPhone(monitoringPhoneValue, monitoringPhoneCountry);
  const showEmailError = normalizedEmail.length > 0 && !emailIsValid;
  const showPhoneError = phoneDigits.length > 0 && !phoneIsValid;
  const showMonitoringPhoneError = !testMyself && monitoringPhoneDigits.length > 0 && !monitoringPhoneIsValid;
  const isValid = name.trim().length > 0 && emailIsValid && phoneIsValid && monitoringPhoneIsValid;

  const submit = async () => {

      const payload = {
        telephone: phone,
        name: name,
        email: normalizedEmail,
        newsletter: newsletter
      }

      const leadResponse = await saveScoutSignupLead(payload)
      if (leadResponse.status !== 200) {
        if (isAccountAlreadyExistsError(leadResponse)) {
          emitSnackbar({message: 'Você já possui uma conta cadastrada com este número. Faça login para continuar.', severity: 'info'})
          onLogin(phone)
        }
        else
          emitSnackbar({message: manageErrors(leadResponse.data), severity: 'error'})
        setLoading(false)
        return
      }

      const response = await requestOtp(payload)
      if (response.status === 200) {
        const data = stampOtpSessionData(response.data, phone)
        setSession({
          ...data,
          adminData: {
            ...payload,
            monitoring_telephone: monitoringPhoneDigits,
            test_myself: testMyself,
          }
        })
        onNext()
      }
      else if (isAccountAlreadyExistsError(response)) {
        emitSnackbar({message: 'Você já possui uma conta cadastrada com este número. Faça login para continuar.', severity: 'info'})
        onLogin(phone)
      }
      else if (isOtpRequestCooldownError(response))
        emitSnackbar({message: getOtpRequestCooldownMessage(null, phone), severity: 'warning'})
      else
        emitSnackbar({message: manageErrors(response.data), severity: 'error'})

      setLoading(false)
  }

  requestSession = submit

  return (
    <div className="w-full max-w-lg mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-xl font-bold text-foreground mb-2">Vamos criar sua conta de administrador

        </h1>
        <p className="text-sm text-muted-foreground">
          Leva menos de 1 minuto. Seu WhatsApp de administrador será usado apenas para acesso seguro. Depois você escolhe qual número da operação deseja acompanhar no Scout.
        </p>
      </div>

      <div className="bg-card rounded-lg border border-border shadow-sm p-6 space-y-5">
        <div className="space-y-1.5">
          <InputLabel htmlFor="name" className="text-sm font-medium text-foreground">Nome completo</InputLabel>
          <Input
            id="name"
            placeholder="Digite seu nome completo"
            value={name}
            onChange={(e: any) => setName(e.target.value)} />

        </div>

        <div className="space-y-1.5">
          <InputLabel htmlFor="email" className="text-sm font-medium text-foreground">E-mail corporativo</InputLabel>
          <Input
            id="email"
            type="email"
            placeholder="seu@empresa.com.br"
            value={email}
            onChange={(e: any) => setEmail(e.target.value)} />
          {showEmailError ? (
            <p className="text-xs text-destructive">Informe um e-mail válido.</p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <InputLabel htmlFor="phone" className="text-sm font-medium text-foreground">WhatsApp do administrador</InputLabel>
          <PhoneInputWithCountry
            id="phone"
            value={phone}
            onChange={setPhone}
            onCountryChange={(country) => setPhoneCountryCode(country.code)} />
          {showPhoneError ? (
            <p className="text-xs text-destructive">Telefone do Brasil deve ter DDD válido, fixo com 8 dígitos ou celular com 9 dígitos.</p>
          ) : null}

          <p className="text-xs text-muted-foreground">
            Usaremos este número apenas para enviar seu token de acesso seguro.
            Ele não será acompanhado no Scout, a menos que você escolha usá-lo no teste.
          </p>
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="test-myself"
            checked={testMyself}
            onChange={({target}) => setTestMyself(!!target.checked)} />

          <label htmlFor="test-myself" className="text-sm text-muted-foreground cursor-pointer leading-5 pt-[9px]">
            Quero usar este mesmo WhatsApp para o primeiro teste.
          </label>
        </div>

        {!testMyself ? (
          <div className="space-y-1.5">
            <InputLabel
              htmlFor="monitoring-phone"
              className="text-sm font-medium text-foreground"
              sx={{ whiteSpace: "normal", overflow: "visible", textOverflow: "unset" }}>
              WhatsApp da operação que será acompanhado
            </InputLabel>
            <PhoneInputWithCountry
              id="monitoring-phone"
              value={monitoringPhone}
              onChange={setMonitoringPhone}
              onCountryChange={(country) => setMonitoringPhoneCountryCode(country.code)} />
            {showMonitoringPhoneError ? (
              <p className="text-xs text-destructive">Telefone do Brasil deve ter DDD válido, fixo com 8 dígitos ou celular com 9 dígitos.</p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              Informe um número usado em conversas corporativas com clientes, vendas, atendimento ou operação. 
              Comece com apenas 1 número: você poderá adicionar outros depois.
            </p>
          </div>
        ) : null}

        <div className="flex items-start space-x-2">
          <Checkbox
            id="newsletter"
            checked={newsletter}
            onChange={({target}) => setNewsletter(!!target.checked)} />

          <label htmlFor="newsletter" className="text-sm text-muted-foreground cursor-pointer leading-5 pt-[9px]">
            Quero receber dicas e novidades por e-mail.
          </label>
        </div>

        <Button
          className="w-full"
          size="large"
          disabled={!isValid || loading}
          variant='contained'
          onClick={submit}>

        {loading ? 'Aguarde':'Continuar e gerar QR Code'}  
        </Button>

        <p className="text-center">
          <button type="button" className="text-sm font-medium text-accent hover:underline" onClick={() => onLogin()}>
            Já tenho conta? Fazer login
          </button>
        </p>

        <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Shield className="h-3.5 w-3.5" />
          <span>Seus dados são protegidos. Nenhum número será acompanhado automaticamente: a conexão só acontece após a leitura do QR Code.</span>
        </div>
      </div>
    </div>);

};

export default Step1AdminData;